import type { ListingFormNormalizedPayload } from './types';
import { buildFinancingSummary } from '@/lib/financingTerms';
import { requiresFinancingTerms } from '@/lib/dealTypes';

export function buildCreateListingPayload(normalized: ListingFormNormalizedPayload) {
  return {
    address: normalized.address,
    city: normalized.city,
    state: normalized.state,
    zip: normalized.zip,
    country: normalized.country,
    currency: normalized.currency,
    price: normalized.price,
    arv: normalized.arv,
    repairs: normalized.repairs,
    beds: normalized.beds,
    baths: normalized.baths,
    sqft: normalized.sqft,
    lot_sqft: normalized.lot_sqft,
    garage_spaces: normalized.garage_spaces,
    year_built: normalized.year_built,
    property_type: normalized.property_type,
    deal_type: normalized.deal_type,
    age_restricted: normalized.age_restricted,
    description: normalized.description,
    status: 'active' as const,
    contact_name: normalized.contact_name,
    contact_phone: normalized.contact_phone,
    contact_email: normalized.contact_email,
    seller_acknowledged: normalized.seller_acknowledged,
    latitude: normalized.latitude,
    longitude: normalized.longitude,
    deal_constraints: normalized.deal_constraints,
    deal_constraints_notes: normalized.deal_constraints_notes,
    financing_terms: normalized.financing,
    clear_financing_terms: normalized.clear_financing_terms,
  };
}

export function buildUpdateListingPayload(normalized: ListingFormNormalizedPayload) {
  return {
    address: normalized.address,
    city: normalized.city,
    state: normalized.state,
    zip: normalized.zip,
    country: normalized.country,
    currency: normalized.currency,
    price: normalized.price,
    arv: normalized.arv,
    repairs: normalized.repairs,
    beds: normalized.beds,
    baths: normalized.baths,
    sqft: normalized.sqft,
    lot_sqft: normalized.lot_sqft,
    garage_spaces: normalized.garage_spaces,
    year_built: normalized.year_built,
    property_type: normalized.property_type,
    deal_type: normalized.deal_type,
    age_restricted: normalized.age_restricted,
    description: normalized.description,
    contact_name: normalized.contact_name,
    contact_phone: normalized.contact_phone,
    contact_email: normalized.contact_email,
    latitude: normalized.latitude,
    longitude: normalized.longitude,
    deal_constraints: normalized.deal_constraints,
    deal_constraints_notes: normalized.deal_constraints_notes,
    financing_terms: normalized.financing,
    clear_financing_terms: normalized.clear_financing_terms,
  };
}

export function buildFinancingTermsUpsert(normalized: ListingFormNormalizedPayload) {
  if (!normalized.deal_type || normalized.clear_financing_terms) return null;
  if (!requiresFinancingTerms(normalized.deal_type)) return null;
  const summary = buildFinancingSummary(normalized.deal_type, normalized.financing);
  return {
    deal_type: normalized.deal_type,
    cash_needed: summary.cash_needed,
    monthly_payment: summary.monthly_payment,
    interest_rate: summary.interest_rate,
    existing_loan_balance: summary.existing_loan_balance,
    down_payment: summary.down_payment,
    balloon_due_date: summary.balloon_due_date,
    terms: summary.terms,
    due_on_sale_acknowledged: summary.due_on_sale_acknowledged,
    updated_at: new Date().toISOString(),
  };
}

export function mergeImageKeysForSave(
  formImages: Array<{ storageKey: string | null; file: File | null }>,
  uploadedKeys: string[],
  coverIndex: number | null
): { images: string[]; cover_image_url: string | null; image_url: string | null } {
  let uploadCursor = 0;
  const finalKeys: string[] = [];

  for (const img of formImages) {
    if (img.file) {
      const key = uploadedKeys[uploadCursor++];
      if (key) finalKeys.push(key);
    } else if (img.storageKey && !img.storageKey.startsWith('blob:')) {
      finalKeys.push(img.storageKey);
    }
  }

  const safeCoverIndex =
    coverIndex != null && coverIndex >= 0 && coverIndex < finalKeys.length ? coverIndex : 0;
  const cover = finalKeys.length > 0 ? finalKeys[safeCoverIndex] ?? finalKeys[0] : null;

  return {
    images: finalKeys,
    cover_image_url: cover,
    image_url: cover,
  };
}
